import { Group, group_query_one, group_upsert, group_update_member_synced } from "../db/t_group";
import Context from "./context";
import {
    group_member_qry,
    group_member_update_mine,
    group_member_update_mine_remark,
    group_member_upsert,
    GroupMember,
    group_member_qry_topic,
} from "../db/t_group_member";
import time from "../time";
import log from "../log";
import {
    group_summary_del,
    group_summary_query_all,
    group_summary_query_by_tid,
    group_summary_update,
    group_summary_upsert,
    GroupSummary,
    GroupSummaryUpdateType,
} from "../db/t_group_summary";
import { config_get, config_set } from "../db/t_config";
import { User } from "../db/t_user";

export class GroupController {
    private static lastRefreshTime: number = 0;
    private static isRefreshing: boolean = false;
    /** 同一 tid 并发 GetRemote（群详情 + topic/user/detail）合并为一次网络请求，避免重复打接口 */
    private static getRemoteInflight = new Map<
        string,
        Promise<{ error: any; group?: Group; members?: GroupMember[] }>
    >();

    public static Clear() {
        GroupController.lastRefreshTime = 0;
        GroupController.isRefreshing = false;
        GroupController.getRemoteInflight.clear();
    }

    public static async QrySummaryList(ctx: Context, sync: boolean = false): Promise<{ error: any; groupSummarys?: GroupSummary[] }> {
        if (!sync) {
            return await group_summary_query_all(ctx.db);
        }
        const r = await GroupController.check_and_refresh_summary(ctx);
        if (r.groupSummarys && r.groupSummarys.length > 0) {
            return r;
        }
        const r1 = await group_summary_query_all(ctx.db);
        if (r1.groupSummarys && r1.groupSummarys.length > 0) {
            return r1;
        }
        return await this.do_refresh_summary(ctx);
    }
    public static async QrySummary(ctx: Context, tid: string): Promise<{ error: any; groupSummarys?: GroupSummary }> {
        return await group_summary_query_by_tid(ctx.db, tid);
    }

    public static async GroupSummaryUpdate(ctx: Context, tid: string, data: GroupSummaryUpdateType): Promise<{ error: any }> {
        return await group_summary_update(ctx.db, tid, data);
    }

    private static async check_and_refresh_summary(ctx: Context): Promise<{
        error: any;
        groupSummarys?: GroupSummary[];
    }> {
        if (GroupController.isRefreshing) {
            return { error: -1 };
        }
        const db_key = "group_summary_fresh_time";
        if (GroupController.lastRefreshTime === 0) {
            const rst = await config_get(ctx.db, db_key);
            if (rst.error) {
                log.error(`check_or_update获取${db_key}配置信息失败！`, rst.error);
                return { error: -1 };
            }
            if (rst.data && rst.data.val) {
                GroupController.lastRefreshTime = parseInt(rst.data.val, 10); // 上一次成功刷新的时间
            }
        }
        // 全量/批量同步会写入 group_summary 但未写 group_summary_fresh_time，lastRefreshTime 仍为 0 时不应误打 group/list
        if (GroupController.lastRefreshTime <= 0 || Number.isNaN(GroupController.lastRefreshTime)) {
            const localR = await group_summary_query_all(ctx.db);
            if (localR.groupSummarys && localR.groupSummarys.length > 0) {
                GroupController.lastRefreshTime = time.toUnixMillSecond(time.now());
                await config_set(ctx.db, db_key, GroupController.lastRefreshTime.toString(10));
                log.info("群摘要本地已有数据，跳过 group/list 远程刷新");
                return { error: -1 };
            }
        }
        // 检查两天内是否刷新过
        if (GroupController.lastRefreshTime > time.toUnixMillSecond(time.add(time.now(), -2 * time.DAY))) {
            log.info("群摘要上一次刷新", time.formatTime(time.fromUnixMillSecond(GroupController.lastRefreshTime)), "无需更新");
            return { error: -1 };
        }

        log.info(
            "群摘要距离上一次会话数据刷新" +
                time.formatTime(time.fromUnixMillSecond(GroupController.lastRefreshTime)) +
                "超过两天了,准备更新"
        );

        // 再次检查
        if (GroupController.isRefreshing) {
            return { error: -1 };
        }
        GroupController.isRefreshing = true;
        const res = await GroupController.do_refresh_summary(ctx);
        GroupController.isRefreshing = false;
        if (!res.error) {
            GroupController.lastRefreshTime = time.toUnixMillSecond(time.now());
            await config_set(ctx.db, db_key, GroupController.lastRefreshTime.toString(10));
        }
        return res;
    }

    private static async do_refresh_summary(ctx: Context): Promise<{ error: any; groupSummarys?: GroupSummary[] }> {
        const r = await ctx.conn.Call("/v1/sdk/group/list", {});
        if (!(r && r.code === 0)) {
            return { error: JSON.stringify(r) };
        }
        if (!(r.data && r.data.records)) {
            return { error: null };
        }
        const records = r.data.records as GroupSummary[];
        const n = records.length;
        for (let i = 0; i < n; i++) {
            await group_summary_upsert(ctx.db, records[i]);
        }

        return { error: null, groupSummarys: records };
    }

    // 返回群详情 优先查本地 member_synced=1 表示成员已同步完整数据，直接用本地
    public static async Detail(
        ctx: Context,
        tid: string,
        sync: boolean
    ): Promise<{
        error: any;
        group?: Group;
        members?: GroupMember[];
    }> {
        const r0 = await GroupController.qryInLocal(ctx, tid);
        if (r0.error) {
            return { error: r0.error };
        }
        // member_synced=1 表示成员数据已同步完整，直接用本地数据
        if (r0.group?.member_synced === 1) {
            return r0;
        }
        // 成员未同步完整，需要远程拉取
        if (sync) {
            const r1 = await GroupController.GetRemote(ctx, tid, r0.group);
            if (!r1.error) {
                return r1;
            }
            return r1;
        } else {
            GroupController.GetRemote(ctx, tid, r0.group).then((res) => {
                ctx.mq.emit("group_update", { group_update: { tid: tid } });
            });
        }
        return r0;
    }

    public static async QryGroupMembersDetail(
        ctx: Context,
        tid: string
    ): Promise<{
        error: any;
        members?: GroupMember[];
    }> {
        const r0 = await this.qryMemberInLocal(ctx, tid);
        if (r0.members && r0.members.length > 0) {
            return r0;
        }
        return await GroupController.GetRemote(ctx, tid);
    }

    /** 仅从 DB 查群信息+成员，不触发远程请求（用于会话列表 get_summary，避免批量远程） */
    public static async QryLocal(
        ctx: Context,
        tid: string
    ): Promise<{
        error: any;
        group?: Group;
        members?: GroupMember[];
    }> {
        const r2 = await group_query_one(ctx.db, tid);
        if (r2.error) {
            return { error: r2.error };
        }
        const r3 = await this.qryMemberInLocal(ctx, tid);
        return {
            error: null,
            group: r2.group,
            members: r3.members,
        };
    }

    private static async qryInLocal(ctx: Context, tid: string) {
        const r2 = await group_query_one(ctx.db, tid);
        if (r2.error) {
            return { error: r2.error };
        }
        const r3 = await this.qryMemberInLocal(ctx, tid);
        return {
            error: null,
            group: r2.group,
            members: r3.members,
        };
    }

    private static async qryMemberInLocal(ctx: Context, tid: string) {
        let members: GroupMember[] = [];
        const r3 = await group_member_qry(ctx.db, tid);
        if (r3.data) {
            members = r3.data;
        }
        return {
            error: null,
            members: members,
        };
    }

    public static async GroupUpsert(ctx: Context, group: Group) {
        return group_upsert(ctx.db, group);
    }
    public static async GroupUserModify(ctx: Context, user: User) {
        return group_member_update_mine(ctx.db, user);
    }

    public static async GroupUserRemarkModify(ctx: Context, uid: number, remark: string) {
        return group_member_update_mine_remark(ctx.db, uid, remark);
    }

    public static async QryGroupTidsByUid(ctx: Context, uid: number): Promise<{ error: any; tids?: string[] }> {
        return group_member_qry_topic(ctx.db, uid);
    }

    public static async GetGroupRemote(
        ctx: Context,
        tid: string,
        oldGroup?: Group
    ): Promise<{
        error: any;
        members?: number[];
        group?: Group;
    }> {
        const r1 = await ctx.conn.Call("/v1/sdk/group/detail", { tid: tid });
        if (!(r1 && r1.code === 0)) {
            return { error: r1 };
        }
        if (!(r1 && r1.data)) {
            return { error: null };
        }
        if (r1.data.group) {
            if (r1.data.group.flag !== 2) {
                const group = r1.data.group as any as Group;
                if (oldGroup && oldGroup.notice === group.notice) {
                    group.notice_read = "0";
                }
                await group_upsert(ctx.db, group);
            }
        }

        return { error: null, members: r1.data.members, group: r1.data.group };
    }

    public static async GetRemote(
        ctx: Context,
        tid: string,
        oldGroup?: Group
    ): Promise<{
        error: any;
        group?: Group;
        members?: GroupMember[];
    }> {
        const inflight = GroupController.getRemoteInflight.get(tid);
        if (inflight) {
            return inflight;
        }
        const p = (async (): Promise<{
            error: any;
            group?: Group;
            members?: GroupMember[];
        }> => {
            try {
                const r1 = await this.GetGroupRemote(ctx, tid, oldGroup);
                let uids: number[] = [];
                if (r1.members) {
                    uids = r1.members as number[];
                }
                let members: GroupMember[] = [];
                const totalMembers = uids.length;
                if (r1.group?.tid && totalMembers > 0) {
                    if (r1.group.flag !== 2) {
                        // 分批获取成员详情，每批 MEMBER_BATCH_SIZE 个 uid
                        const BATCH_SIZE = 50;
                        const allUsers: GroupMember[] = [];
                        for (let i = 0; i < totalMembers; i += BATCH_SIZE) {
                            const batchUids = uids.slice(i, i + BATCH_SIZE);
                            const r2 = await GroupController.GetUserRemote(ctx, tid, batchUids);
                            if (r2.data && r2.data.users && r2.data.users.length > 0) {
                                for (const user of r2.data.users) {
                                    const member = user as GroupMember;
                                    member.tid = tid;
                                    allUsers.push(member);
                                }
                            }
                        }
                        members = allUsers;
                        if (allUsers.length > 0) {
                            await group_member_upsert(ctx.db, allUsers);
                        }
                        // 标记成员已同步完整数据，同时更新 member_count
                        await group_update_member_synced(ctx.db, tid, totalMembers);
                    } else {
                        group_summary_del(ctx.db, r1.group?.tid);
                    }
                } else if (r1.group?.tid && totalMembers === 0) {
                    // 群存在但无成员（理论上不应该），也标记已同步
                    await group_update_member_synced(ctx.db, tid, 0);
                }
                return { error: null, group: r1.group, members: members };
            } finally {
                GroupController.getRemoteInflight.delete(tid);
            }
        })();
        GroupController.getRemoteInflight.set(tid, p);
        return p;
    }

    public static async GetUserRemote(ctx: Context, tid: string, uids: number[]) {
        if (tid && uids.length > 0) {
            return await ctx.conn.Call("/v1/sdk/topic/user/detail", { tid: tid, uids: uids });
        }
        return { error: null };
    }
}
